import React from 'react';
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import { NavLink } from 'react-router-dom';
import moment from 'moment';

import Block from 'components/common/block';
import Tag from 'components/common/tag';
import Pagination from 'components/common/pagination';
import JobLogModal from 'components/common/equipment/hoses/jobLogModal';
import { ReactComponent as EditIcon } from 'assets/vectors/edit-16px.svg';


const GetHoseJobsQuery = gql`
  query StsProjectEquipments ($first: Int, $after: String, $last: Int, $before: String, $sortBy: [String], $hoseId: ID){
    stsprojectEquipments(first: $first, after: $after, last: $last, before: $before, sortBy: $sortBy, hoseId: $hoseId) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        endCursor
        startCursor
      }
      edges {
        node {
          id
          customProjectId
          customProduct
          customLocation
          customStartDate
          customEndDate
          stsproject {
            id
            projectId
            product
            location
            startDate
            endDate
            dateStatus
          }
        }
      }
    }
  }
`;

function tagColor(status) {
  switch(status) {
    case 'planned':
      return 'yellow';
    case 'finished':
      return 'blue';
    default:
      return 'green'
  }
}

const resultCount = 10;

function JobLogOverview({ id }) {
  const [showJobLogModal, setShowJobLogModal] = React.useState(false);
  const [paginationVariables, setPaginationVariables] = React.useState({first: resultCount});

  const { data, error, loading, refetch } = useQuery(GetHoseJobsQuery, {
    variables: {
      hoseId: id,
      ...paginationVariables,
    },
    skip: !id,
    fetchPolicy: 'network-only',
  });


  if (loading) return <CircularProgress size={64} style={{ margin: '32px auto', display: 'block' }} />;
  if (error || !data.stsprojectEquipments) return null;

  const jobs = data.stsprojectEquipments.edges.map(j => j.node);
  const pageInfo = data ? data.stsprojectEquipments.pageInfo : {};

  return (
    <StyledBlock title="JOB LOG">
      <TableContainer>
        <Table style={{ whiteSpace: 'nowrap' }}>
          <TableHead>
            <TableRow>
              <TableCell>
                Project ID
              </TableCell>
              <TableCell>
                Product
              </TableCell>
              <TableCell>
                Location
              </TableCell>
              <TableCell>
                Start Date
              </TableCell>
              <TableCell>
                End Date
              </TableCell>
              <TableCell>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              !jobs.length
                ? <TableRow>
                    <TableCell>No results</TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell />
                  </TableRow>
                : (
                  jobs.map((job) => (
                    <TableRow key={job.id}>
                      {
                        job.stsproject
                          ? <>
                              <TableCell>
                                <Link to={`/dashboard/sts/${job.stsproject.id}`}>{job.stsproject.projectId}</Link>
                              </TableCell>
                              <TableCell>
                                {job.stsproject.product}
                              </TableCell>
                              <TableCell>
                                {job.stsproject.location}
                              </TableCell>
                              <TableCell>
                                {moment(job.stsproject.startDate).format('DD/MM/YYYY')}
                              </TableCell>
                              <TableCell>
                                {moment(job.stsproject.endDate).format('DD/MM/YYYY')}
                              </TableCell>
                              <TableCell>
                                <Tag color={tagColor(job.stsproject.dateStatus)} label={job.stsproject.dateStatus} />
                              </TableCell>
                            </>
                          : <>
                              <TableCell>{job.customProjectId}</TableCell>
                              <TableCell>{job.customProduct}</TableCell>
                              <TableCell>{job.customLocation}</TableCell>
                              <TableCell>{moment(job.customStartDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}</TableCell>
                              <TableCell>{moment(job.customEndDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}</TableCell>
                              <TableCell />
                            </>
                      }
                      
                    </TableRow>
                  ))
                )
            }
          </TableBody>
        </Table>
      </TableContainer>
      <ButtonWrapper>
        <Button
          variant="outlined"
          color="primary"
          endIcon={<EditIcon />}
          style={{ marginRight: '8px', textDecoration: 'none', marginTop: '16px' }}
          onClick={() => setShowJobLogModal(true)}
        >Job log</Button>
        <Pagination
          paginationVariables={paginationVariables}
          setPaginationVariables={setPaginationVariables}
          resultCount={resultCount}
          pageInfo={pageInfo}
        />
      </ButtonWrapper>
      {showJobLogModal && <JobLogModal hoseId={id} setShowJobLogModal={setShowJobLogModal} refetchJobs={refetch} />}
    </StyledBlock>
  )
}

const StyledBlock = styled(Block)`
  max-width: 100%;
  margin-bottom: 16px;
`;

const Link = styled(NavLink)`
  color: ${props => props.theme.colors.primaryColor};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

export default JobLogOverview;
